import type { Runner } from '../types/runner.ts'

type TimerIncrementedListener = {
    onIncrement(seconds: number): void
}

/** @internal */
export type TimerOptions = {
    /**
     * A boolean flag that determines whether the Timer should start immediately
     * or wait until manually started.
     *
     * When `true`, or if not provided, the Timer will immediately start. When `false`
     * the timer will wait until [Timer._start()]{@link _start} is invoked.
     *
     * @example
     * ```typescript
     * // Manually started task
     * const timer = new Timer(myTask, 1)
     * timer._start()
     * ```
     *
     * @default true
     */
    autoStart?: boolean
    /**
     * A boolean flag that determines whether the specified {@link TimerTask} should
     * run once or continue to run on each eligible interval.
     *
     * @example
     * ```typescript
     * // Single-run task
     * new Timer(myTask, 1, { repeats: false })
     * ```
     *
     * @default true
     */
    repeats?: boolean
    /**
     * A boolean flag that determines whether the specified {@link TimerTask}
     * should run immediately when the Timer starts.
     *
     * @default false
     */
    runImmediatelyOnStart?: boolean
}

/** @internal */
export type TimerTask = ((timer: Timer) => void) | Runner

/** @internal */
export class Timer {
    public readonly _name: string
    public readonly _task: TimerTask
    public readonly _intervalMilliseconds: number
    public readonly _options: TimerOptions

    protected _timer: any
    protected _isStarted: boolean
    protected _isPaused: boolean
    protected _startTime: number
    protected _stopTime: number
    protected _pausedStartTime: number
    protected _totalPausedTime: number
    protected _expectedTime: number
    protected _timerDidIncrementListeners: Set<TimerIncrementedListener>

    /**
     * @param {string} name - Unique name of the timer instance.
     * @param {TimerTask} task - Task to be run on each tick.
     * @param {number} [intervalSeconds=1]
     * @param {TimerOptions} [options={ delayStartSeconds: 0, autoStart: true, repeats: true, runImmediatelyOnStart: false }]
     */
    public constructor(name: string, task: TimerTask, intervalSeconds?: number, options?: TimerOptions) {
        this._name = name
        this._task = task
        this._intervalMilliseconds = (intervalSeconds ?? 1) * 1000
        this._options = options ?? {}

        this._isStarted = false
        this._isPaused = false
        this._startTime = 0
        this._stopTime = 0
        this._pausedStartTime = 0
        this._totalPausedTime = 0
        this._expectedTime = 0
        this._timerDidIncrementListeners = new Set()

        if (options?.autoStart !== false) {
            this._start()
        }
    }

    public get repeats(): boolean {
        return this._options?.repeats !== false
    }

    public get runtimeMilliseconds() {
        if (!this._startTime) {
            return 0
        }

        const latestTime = this._stopTime || Date.now()
        let elapsedTime = latestTime - this._startTime - this._totalPausedTime

        // If currently paused, subtract the current pause duration
        if (this._isPaused) {
            elapsedTime -= latestTime - this._pausedStartTime
        }

        return elapsedTime
    }

    public get runtimeSeconds() {
        return Math.floor(this.runtimeMilliseconds / 1000)
    }

    public get isStarted(): boolean {
        return this._isStarted
    }

    public get isPaused(): boolean {
        return this._isPaused
    }

    public _addTimerDidIncrementListener(listener: TimerIncrementedListener) {
        this._timerDidIncrementListeners.add(listener)
    }

    public _removeTimerDidIncrementListener(listener: TimerIncrementedListener) {
        this._timerDidIncrementListeners.delete(listener)
    }

    public _start() {
        if (this.isStarted) {
            return
        }

        this._isStarted = true
        this._startTime = Date.now()
        this._stopTime = 0
        this._expectedTime = this._startTime

        if (this._options.runImmediatelyOnStart) {
            this._executeTask()
        }

        this._run()
    }

    public _stop() {
        if (!this.isStarted) {
            return
        }

        clearTimeout(this._timer)

        this._stopTime = Date.now()
        this._isStarted = false
        this._timer = undefined
    }

    public _pause() {
        if (!this.isStarted || this.isPaused) {
            return
        }

        clearTimeout(this._timer)

        this._pausedStartTime = Date.now()
        this._isPaused = true
        this._timer = undefined
    }

    public _unpause() {
        if (!this.isStarted || !this.isPaused) {
            return
        }

        this._isPaused = false

        // Calculate the time spent in pause
        const pauseDuration = Date.now() - this._pausedStartTime
        this._totalPausedTime += pauseDuration
        // Adjust the expected time to account for the pause duration
        this._expectedTime += pauseDuration

        this._run()
    }

    protected _run() {
        if (!this.isStarted || this.isPaused) {
            return
        }

        const currentTime = Date.now()
        const drift = currentTime - this._expectedTime

        // Calculate the next tick time, accounting for drift
        this._expectedTime += this._intervalMilliseconds

        // Adjust timeout to maintain interval accuracy
        const nextTick = Math.max(0, this._intervalMilliseconds - drift)

        this._timer = setTimeout(() => {
            this._executeTask()
            this._timerDidIncrementListeners.forEach((it) => {
                it.onIncrement(this.runtimeSeconds)
            })

            this.repeats ? this._run() : this._stop()
        }, nextTick)
    }

    protected _executeTask() {
        if (typeof this._task === 'function') {
            this._task(this)
        } else {
            this._task._run(this)
        }
    }
}
